import { z } from 'zod';

import {
  aiPromptDescriptorSchema,
  aiPromptIssueSchema,
  aiPromptKeySchema,
} from '../ai-prompts';
import { idSchema, isoDateTimeSchema } from '../common';

/**
 * `/ai-prompts` DTOs — the prompt management tab.
 *
 * The resource is deliberately thin: the *catalog* (labels, descriptions, legal
 * blocks, default text) is compiled into `contracts` and therefore already
 * present in the dashboard bundle, so these responses carry only what the server
 * actually knows — which workspace overrode what, when, and by whom.
 *
 * It is still returned on GET rather than left to the client to look up locally.
 * A dashboard build can be older than the API it is talking to, and a tab that
 * silently renders yesterday's block list would let an operator save a template
 * referencing a block the server has since removed. Serving the catalog makes the
 * server's copy the one on screen.
 *
 * THE PROMPT IS NOT A SECRET, BUT IT IS A LEVER. These routes are OWNER/ADMIN for
 * the same reason `/ai-provider` is: the comment prompt decides what gets typed
 * into Facebook under a real person's name, and the scoring prompt decides which
 * posts are worth money. Neither is something a MEMBER should be able to rewrite,
 * and the diff between "what we shipped" and "what this workspace runs" is
 * exactly the reconnaissance an attacker with a stolen session would want.
 */

/**
 * An edited template.
 *
 * Bounded generously here and precisely in {@link validateAiPromptTemplate},
 * which knows each prompt's own ceiling. This bound exists so a multi-megabyte
 * body is rejected by the pipe before any of it is scanned for placeholders.
 */
export const aiPromptTemplateSchema = z.string().min(1).max(40_000);

// ---------------------------------------------------------------------------
// One prompt, as the tab shows it
// ---------------------------------------------------------------------------

export const aiPromptStateSchema = z.object({
  key: aiPromptKeySchema,
  /** The full catalog entry, so the editor needs no local table. */
  descriptor: aiPromptDescriptorSchema,
  /**
   * The text this workspace actually sends — the override when one exists, the
   * shipped default otherwise.
   *
   * One field rather than "default plus optional override" because every
   * consumer wants the effective answer, and computing it in three places is how
   * the preview stops matching what the worker sends. The default is still
   * reachable at `descriptor.defaultTemplate` for the reset button and the diff.
   */
  template: z.string().min(1),
  /** True when `template` came from `ai_prompt_templates` rather than the catalog. */
  isOverridden: z.boolean(),
  /** Null while the prompt is untouched. */
  updatedAt: isoDateTimeSchema.nullable(),
  /** Who last saved it. Null for an untouched prompt. */
  updatedByUserId: idSchema.nullable(),
  /** Display name of that user, when the row still exists. */
  updatedByName: z.string().nullable(),
});
export type AiPromptState = z.infer<typeof aiPromptStateSchema>;

// ---------------------------------------------------------------------------
// GET /ai-prompts
// ---------------------------------------------------------------------------

export const getAiPromptsResponseSchema = z.object({
  /** Every prompt, in catalog order. Overridden and untouched alike. */
  prompts: z.array(aiPromptStateSchema).min(1),
  /** How many carry a workspace override. Drives the "đã tuỳ chỉnh" badge. */
  overriddenCount: z.number().int().nonnegative(),
});
export type GetAiPromptsResponse = z.infer<typeof getAiPromptsResponseSchema>;

// ---------------------------------------------------------------------------
// PUT /ai-prompts/:key
// ---------------------------------------------------------------------------

export const putAiPromptRequestSchema = z.object({
  template: aiPromptTemplateSchema,
});
export type PutAiPromptRequest = z.infer<typeof putAiPromptRequestSchema>;

export const putAiPromptResponseSchema = z.object({
  prompt: aiPromptStateSchema,
  /**
   * Problems that did **not** block the save.
   *
   * Empty in practice today — every issue the validator produces is refused with
   * a 400 — and present anyway so a future advisory check (a prompt that no
   * longer mentions the JSON requirement, say) has somewhere to land that is not
   * a rejection. A client must render these as warnings, never as failures.
   */
  warnings: z.array(aiPromptIssueSchema),
});
export type PutAiPromptResponse = z.infer<typeof putAiPromptResponseSchema>;

// ---------------------------------------------------------------------------
// DELETE /ai-prompts/:key  (reset to the shipped default)
// ---------------------------------------------------------------------------

/**
 * Resetting deletes the override row rather than writing the current default
 * into it.
 *
 * The difference matters on the next deploy: a workspace that reset in order to
 * "go back to standard" expects to keep receiving improvements to the standard
 * prompt. Materialising today's default would freeze them on it forever, and
 * they would have no way to tell — the tab would show text identical to the
 * default while quietly no longer tracking it.
 */
export const deleteAiPromptResponseSchema = z.object({
  prompt: aiPromptStateSchema,
  /** False when there was nothing to reset; the call is idempotent either way. */
  wasOverridden: z.boolean(),
});
export type DeleteAiPromptResponse = z.infer<typeof deleteAiPromptResponseSchema>;
